class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};

    // Ensure that all DOM queries only happen in the scope of this component instance
    this.input = this.querySelector('input[type="search"]');
    this.searchInput = this.querySelector(".search-input"); // fallback, some themes call it this
    this.submitBtn = this.querySelector(".search-submit");
    this.clearBtn = this.querySelector(".clear-btn");
    this.predictiveSearchResults = this.querySelector("#predictive-search");
    this.predictiveSearchQueries = this.querySelector("#predictive-queries");
    this.abortController = new AbortController();

    // Show/hide predictive query suggestions if they exist, scoped to this instance
    if (this.predictiveSearchQueries && this.input) {
      const toggleQueries = () => {
        if (this.input.value.length === 0) {
          this.predictiveSearchQueries.style.display = "";
        } else {
          this.predictiveSearchQueries.style.display = "none";
        }
      };
      this.input.addEventListener("input", toggleQueries);
      toggleQueries();
    }

    // Listen for submit click (scoped)
    if (this.submitBtn) {
      this.submitBtn.addEventListener("click", (event) => {
        event.preventDefault();
        this.onChange(event);
      });
      // Keypress on submit for Enter/backspace/etc (scoped)
      this.submitBtn.addEventListener(
        "keypress",
        this.debounce((event) => {
          if (
            event.key === "Enter" ||
            event.keyCode === 8 ||
            event.keyCode === 46
          ) {
            event.preventDefault();
            this.onChange(event);
          }
        }, 300).bind(this)
      );
    }

    // Listen for input and key events (scoped)
    if (this.input) {
      // All input events for normal typing
      this.input.addEventListener(
        "input",
        this.debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
      // Enter navigates to search page
      this.input.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          window.location.href = `/search?type=product&options[unavailable_products]=show&q=${encodeURIComponent(this.input.value)}`;
        }
      });
      // Listen for Backspace/Delete on the input so results clear as the user erases their query
      this.input.addEventListener(
        "keyup",
        this.debounce((event) => {
          if (
            event.key === "Backspace" ||
            event.key === "Delete" ||
            event.keyCode === 8 ||
            event.keyCode === 46
          ) {
            this.onChange(event);
          }
        }, 300).bind(this)
      );
    }

    // Listen for search tag clicks within this instance
    const searchTags = this.querySelectorAll(".search-tag");
    searchTags.forEach((tag) => {
      tag.addEventListener(
        "click",
        this.debounce((event) => {
          // Set value and placeholder on correct input
          if (this.input) {
            this.input.value = tag.dataset.search || tag.innerHTML;
            this.input.placeholder = tag.dataset.search;
          }
          this.onTag(tag.dataset.search);
          if (this.clearBtn) {
            this.clearBtn.classList.add("active");
          }
        }, 300).bind(this)
      );
    });

    // Listen for clear btn click within this instance
    if (this.clearBtn) {
      this.clearBtn.addEventListener(
        "click",
        this.debounce((event) => {
          event.preventDefault();
          if (this.input) this.input.value = "";
          this.onChange(event);
          this.clearBtn.classList.remove("active");
        }, 300).bind(this)
      );
    }
    //Toggle add to wishlist modal (unchanged code, but comment preserved)
  }

  onTag(e) {
    const searchTerm = e;
    if (!searchTerm || !searchTerm.length) {
      this.clearResults();
      this.close();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onChange() {
    const searchTerm = this.input ? this.input.value.trim() : "";
    if (!searchTerm.length) {
      this.clearResults();
      this.close();
      if (this.clearBtn) this.clearBtn.classList.remove("active");
      return;
    }
    this.open();
    if (this.clearBtn) this.clearBtn.classList.add("active");
    this.getSearchResults(searchTerm);
  }

  clearResults() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = "";
      this.predictiveSearchResults.style.display = "none";
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/ /g, "-").toLowerCase();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    fetch(
      `/search/suggest?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[type]=product&resources[options][unavailable_products]=show&resources[limit_scope]=each`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        if (window.matchMedia("(max-width: 769px)").matches) {
          // Only hide suggestions if they exist in this panel
          if (this.predictiveSearchQueries) {
            this.predictiveSearchQueries.style.display = "none";
          }
        }
        const wrapper = new DOMParser().parseFromString(text, "text/html");
        const foundSection = wrapper.querySelector(
          "#shopify-section-predictive-search"
        );
        if (this.predictiveSearchResults && foundSection) {
          this.predictiveSearchResults.innerHTML = foundSection.innerHTML;
        }
        this.open();
      })
      .catch((error) => {
        this.clearResults();
        throw error;
      });
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = "block";
    }
    this.setAttribute("open", true);
  }
  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }
  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = "";
      this.setAttribute("open", false);
    }
    this.clearResults();
  }

  // If renderSearchResults is needed (for cache), provide fallback
  renderSearchResults(resultsHtml) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsHtml;
      this.open();
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
